/**
 * notifications-feed.ts — Cliente API pra feed unificado de notificacoes.
 *
 * Spec docs/notificacoes/IMPLEMENTATION_SPEC_V1V2.md §3.3.
 *
 * Endpoints cobertos:
 * - GET    /v1/me/notifications/feed (cursor + filtros)
 * - PATCH  /v1/me/notifications/feed/mark-read         { event_ids }
 * - PATCH  /v1/me/notifications/feed/mark-all-read
 * - PATCH  /v1/me/notifications/feed/dismiss           { event_ids }
 * - POST   /v1/me/notifications/feed/{id}/acknowledge  { note? }
 */
import { api } from '@/api/client'

export type Severity = 'debug' | 'info' | 'warning' | 'critical' | 'fatal'

export type FeedStatus = 'unread' | 'read' | 'all' | 'dismissed'

export interface FeedItem {
  event_id: number
  source: string
  event_type: string
  severity: Severity
  tenant_id: number | null
  title: string
  message: string | null
  link: string | null
  occurred_at: string
  delivered_at: string | null
  read_at: string | null
  dismissed_at: string | null
  acknowledged_at: string | null
  source_meta?: Record<string, unknown>
  coalesced_count?: number
}

export interface FeedFilters {
  status?: FeedStatus
  sources?: string[]
  severity?: Severity[]
  date_from?: string
  date_to?: string
  q?: string
  tenant_id?: number
  view_id?: number
  limit?: number
  cursor?: string
}

export interface FeedResponse {
  items: FeedItem[]
  next_cursor: string | null
  total_unread?: number
}

function buildParams(f: FeedFilters): Record<string, string> {
  const p: Record<string, string> = {}
  if (f.status) p.status = f.status
  if (f.sources && f.sources.length > 0) p.sources = f.sources.join(',')
  if (f.severity && f.severity.length > 0) p.severity = f.severity.join(',')
  if (f.date_from) p.date_from = f.date_from
  if (f.date_to) p.date_to = f.date_to
  if (f.q) p.q = f.q
  if (f.tenant_id != null) p.tenant_id = String(f.tenant_id)
  if (f.view_id != null) p.view_id = String(f.view_id)
  if (f.limit != null) p.limit = String(f.limit)
  if (f.cursor) p.cursor = f.cursor
  return p
}

export const notificationsFeedApi = {
  async list(filters: FeedFilters = {}): Promise<FeedResponse> {
    const { data } = await api.get<FeedResponse>('/v1/me/notifications/feed', {
      params: buildParams(filters),
    })
    return data
  },

  async markRead(event_ids: number[]): Promise<{ marked_count: number }> {
    const { data } = await api.patch<{ marked_count: number }>(
      '/v1/me/notifications/feed/mark-read',
      { event_ids },
    )
    return data
  },

  async markAllRead(): Promise<{ marked_count: number }> {
    const { data } = await api.patch<{ marked_count: number }>(
      '/v1/me/notifications/feed/mark-all-read',
    )
    return data
  },

  async dismiss(event_ids: number[]): Promise<{ dismissed_count: number }> {
    const { data } = await api.patch<{ dismissed_count: number }>(
      '/v1/me/notifications/feed/dismiss',
      { event_ids },
    )
    return data
  },

  async acknowledge(event_id: number, note?: string): Promise<{ acknowledged: boolean }> {
    const { data } = await api.post<{ acknowledged: boolean }>(
      `/v1/me/notifications/feed/${event_id}/acknowledge`,
      { note },
    )
    return data
  },
}
